<script setup lang="ts">
import { computed } from 'vue';

import { EntityPresenterV2Mode } from '@satellite/composables/presenterV2EntityMode';
import { useYardAreaFetcherV2 } from '@satellite/composables/useYardAreaFetcherV2';
import { useYardAreaDisplayV2 } from '@satellite/composables/useYardAreaDisplayV2';
import YardAreaDisplayV2 from '@satellite/components/elements/presenters/YardAreaDisplayV2.vue';

import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterV2Placeholder';
import type {
  YardAreaDisplayField,
  UseYardAreaDisplayV2Options
} from '@satellite/composables/useYardAreaDisplayV2';
import type { YardAreaDetails } from '@satellite/composables/yardAreaPresenterV2Shared';

const {
  size = 'regular',
  areaId = null,
  initialAreaData = null,
  placeholderAreaData = null,
  staticAreaData = null,
  mode = EntityPresenterV2Mode.Live,
  displayFields = ['name'],
  nameProps = {}
} = defineProps<{
  size?: 'regular' | 'small' | 'large';
  areaId?: Maybe<string>;
  initialAreaData?: Maybe<YardAreaDetails>;
  placeholderAreaData?: Maybe<PresenterPlaceholderData<YardAreaDetails>>;
  staticAreaData?: Maybe<YardAreaDetails>;
  mode?: EntityPresenterV2Mode;
  displayFields?: YardAreaDisplayField[];
  nameProps?: UseYardAreaDisplayV2Options['nameProps'];
}>();

const {
  data: area,
  isLoading,
  isFetching,
  onIntersect
} = useYardAreaFetcherV2({
  areaId: computed(() => areaId),
  initialAreaData: computed(() => initialAreaData),
  placeholderAreaData: computed(() => placeholderAreaData),
  staticAreaData: computed(() => staticAreaData),
  mode
});

const data = useYardAreaDisplayV2(area, {
  size: computed(() => size),
  mode: computed(() => mode),
  isFetching,
  nameProps: computed(() => nameProps)
});
</script>

<template>
  <v-skeleton-loader
    :loading="isLoading"
    type="text"
    v-intersect="{
      handler: onIntersect,
      options: {
        threshold: [0.5]
      }
    }">
    <yard-area-display-v2
      class="d-flex flex-column gap-1 yard-area-presenter"
      :data="data"
      :display-fields="displayFields"
      :style="isLoading ? 'min-width: 50px' : ''">
      <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
        <slot :name="slotName" v-bind="slotProps ?? {}" />
      </template>
    </yard-area-display-v2>
  </v-skeleton-loader>
</template>

<style scoped>
:deep(.v-skeleton-loader__text) {
  margin: 0 auto !important;
  height: 1em !important;
}
</style>
